import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchCases, getHeaders, linkCaseToCustomer } from '../api';
import { useViewContext } from '../lib/viewContext';
import { caseAudience, customerReach, contactLabel, deliveryText, closeConfirmText } from '../lib/caseVisibility';
import ReplyMarkup from '../components/ReplyMarkup';
import { AttachmentCard, Lightbox, filesMentioned, humanBytes } from '../components/CaseAttachments';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

function Loading() {
  return <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function priorityBadge(p) {
  const map = { 'High': 'badge-red', 'Medium': 'badge-yellow', 'Low': 'badge-green' };
  return map[p] || 'badge-gray';
}

function statusBadge(s) {
  const map = { 'New': 'badge-blue', 'Working': 'badge-yellow', 'Escalated': 'badge-red', 'Closed': 'badge-green' };
  return map[s] || 'badge-gray';
}

// Salesforce's CaseComment.CommentBody ceiling. The server states the same
// number in GET /api/cases/:id `limits.commentMaxLength` and refuses a longer
// body with 400 too_long; this is the fallback while that response is in
// flight, and the number the counter under the composer counts against.
const COMMENT_MAX_FALLBACK = 4000;

// ─── Who wrote a comment ──────────────────────────────────────────────────────
// CaseComment records the integration user as the author of everything the
// product writes, so squish-backend prefixes every CUSTOMER-authored comment
// with a marker line (lib/customer-case-access.js formatCustomerNote). These
// are that marker's two spellings, and the status-trail lines the customer's
// close/reopen leave behind. Kept in step with the backend by hand — if the
// marker changes there, customer notes here fall back to looking like ours,
// which is wrong but not dangerous.
const CUSTOMER_NOTE_RE = /^Note (?:from|added by Squish support on behalf of) (.+?) \(via ([^)]{1,60})\):\r?\n/;
const ACTIVITY_RE = /^(?:Closed|Reopened) by the customer via /;

function classifyComment(raw) {
  const text = typeof raw === 'string' ? raw : '';
  const m = text.match(CUSTOMER_NOTE_RE);
  if (m) return { author: 'customer', authorName: m[1] || null, via: m[2] || null, onBehalf: /on behalf of/.test(m[0]), body: text.slice(m[0].length) };
  if (ACTIVITY_RE.test(text)) return { author: 'activity', authorName: null, via: null, body: text };
  return { author: 'support', authorName: null, via: null, body: text };
}

// ─── Who the next thing you type is for ──────────────────────────────────────
// Two audiences share one box, so the box has to say which one it is pointed at
// BEFORE anything is typed — not in a checkbox under the Send button, where the
// answer arrives after the words do.
//
// Internal is first and is the default. Every note ever written here was
// internal (the CRM hardcoded isPublic:false until 2026-09-08), agents have
// years of habit aimed at this box, and the asymmetry is total: an internal
// note published by mistake cannot be recalled, while a reply sent as an
// internal note is simply re-sent.
//
// `reachable` false greys out the customer tab. See customerReach — a case
// with neither a Contact nor a SuppliedEmail has nobody to post to, so "public"
// there would post into a room with nobody in it.
function VisibilityTabs({ value, onChange, reachable }) {
  const tab = (key, label, hint) => {
    const active = value === key;
    const disabled = key === 'customer' && !reachable;
    return (
      <button
        key={key}
        onClick={() => !disabled && onChange(key)}
        disabled={disabled}
        title={disabled ? 'This case has no contact and no email address, so a reply has nobody to reach' : hint}
        style={{
          flex: 1, padding: '7px 12px', fontSize: 11, fontWeight: 600,
          fontFamily: 'var(--mono)', letterSpacing: '0.04em',
          background: active ? 'var(--bg3)' : 'transparent',
          color: disabled ? 'var(--text3)' : active ? (key === 'customer' ? 'var(--accent)' : 'var(--text)') : 'var(--text3)',
          border: 'none', borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1, transition: 'all 0.15s',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{
      display: 'flex', gap: 2, padding: 2, flex: '1 1 320px', maxWidth: 420,
      background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7,
    }}>
      {tab('internal', '🔒 Internal note', 'Staff only')}
      {tab('customer', '🌐 Reply to customer', 'The customer reads this on their dashboard and gets it by email')}
    </div>
  );
}

// ─── Reply address ────────────────────────────────────────────────────────────
// Who to answer, shown on every case.
//
// Cases opened from the public help form (squish.co/help, signed out) have no
// Contact — the reply address lives in SuppliedEmail and nowhere else. Until
// this existed the case query didn't even fetch it, so an agent opening one of
// those saw a ticket from nobody with no way to respond: we captured the
// address at intake and then hid it from the only person who needed it.
//
// Contact.Email wins when present (a signed-in customer is the stronger
// identity); SuppliedEmail is the fallback. The no-address branch is loud on
// purpose — that case cannot be answered, and the agent should find that out
// here rather than after writing a reply.
function replyTarget(c) {
  const email = c?.Contact?.Email || c?.SuppliedEmail || null;
  const name  = c?.Contact
    ? [c.Contact.FirstName, c.Contact.LastName].filter(Boolean).join(' ')
    : c?.SuppliedName;
  return { email, name: name || null };
}

function ReplyAddress({ c }) {
  const { email, name } = replyTarget(c);

  if (!email) {
    return (
      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--red, #f87171)', marginTop: 4 }}>
        ⚠ No reply address on this case
      </div>
    );
  }

  return (
    <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 4 }}>
      Reply to{' '}
      <a href={`mailto:${email}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
        {email}
      </a>
      {name ? ` · ${name}` : ''}
      {!c.ContactId && c.SuppliedEmail ? ' · not a known contact' : ''}
    </div>
  );
}

// ─── Who can see this case ────────────────────────────────────────────────────
// "No reply address" said the agent could not answer; it did not say the
// customer could not SEE the ticket, or offer the fix. #00001057 sat like that:
// opened for Cory, linked to nobody, invisible on his dashboard.
// lib/caseVisibility.js decides the kind; this says it and offers the fix.
function AudienceLine({ audience, onLink }) {
  const btn = {
    marginLeft: 8, padding: '2px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)',
    background: 'transparent', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 5, cursor: 'pointer',
  };
  const line = (color, text, action) => (
    <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color, marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
      <span>{text}</span>
      {action && <button data-write type="button" onClick={onLink} style={btn}>{action}</button>}
    </div>
  );
  if (audience.kind === 'customer') return line('var(--accent3)', '👁 Customer sees this ticket');
  if (audience.kind === 'silent') return line('var(--text2)', '🔒 Silent ticket: the customer can\'t see it and hasn\'t been told', audience.canLink && 'Tell customer');
  if (audience.kind === 'unlinked') return line('var(--badge-yellow)', '⚠ The customer can\'t see this: it isn\'t linked to their contact', 'Link to customer');
  return null;
}

// The inline form behind Link to customer / Tell customer. Tries the account's
// only contact first; when the account has several the backend answers
// CONTACT_REQUIRED with the list, and the form asks which.
function LinkCustomerForm({ caseId, silent, onDone, onCancel }) {
  const [notify, setNotify]       = useState(true);
  const [contacts, setContacts]   = useState(null);
  const [contactId, setContactId] = useState('');
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  const submit = async () => {
    if (contacts && !contactId) { setErr('Pick the contact this ticket is for.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await linkCaseToCustomer(caseId, { notify, contactId: contactId || null });
      const who = contactLabel(r.contact) || 'the customer';
      onDone(`Linked to ${who}${r.wasSilent ? ' (no longer silent)' : ''}. ${notify ? `Ticket number ${deliveryText(r)}.` : 'Not emailed.'}`);
    } catch (e) {
      if (e.code === 'CONTACT_REQUIRED' && e.contacts?.length) { setContacts(e.contacts); setErr(''); }
      else setErr(e.message);
    }
    setBusy(false);
  };

  const field = { padding: '4px 8px', fontSize: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontFamily: 'var(--font)' };
  return (
    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
      {contacts && (
        <select value={contactId} onChange={e => setContactId(e.target.value)} style={field} aria-label="Contact">
          <option value="">Which contact?</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{contactLabel(c)}</option>)}
        </select>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)', cursor: 'pointer' }}>
        <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
        Email them the ticket number
      </label>
      <button data-write type="button" onClick={submit} disabled={busy} style={{ padding: '4px 12px', fontSize: 12, fontWeight: 700, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'Linking…' : silent ? 'Tell customer' : 'Link'}
      </button>
      <button type="button" onClick={onCancel} disabled={busy} style={{ padding: '4px 10px', fontSize: 12, background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer' }}>Cancel</button>
      {err && <span style={{ color: 'var(--red, #f87171)', fontSize: 11 }}>⚠ {err}</span>}
    </div>
  );
}

// ─── Composer toolbar ─────────────────────────────────────────────────────────
// Buttons that write reply markup (src/lib/replyMarkup.js) into the textarea
// around the current selection. The markup is what the customer's dashboard
// and the case-reply email render, so the rep never has to remember it — but
// it stays visible in the box, so what is about to go out is never hidden
// behind a rich-text surface that renders one way here and another there.
function ToolButton({ label, title, onClick, active, children }) {
  return (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()} // keep the textarea's selection
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        minWidth: 30, height: 28, padding: '0 8px', fontSize: 12, lineHeight: 1,
        background: active ? 'var(--bg3)' : 'transparent', color: active ? 'var(--text)' : 'var(--text2)',
        border: '1px solid transparent', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--font)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text2)'; } }}
    >
      {children || label}
    </button>
  );
}

// A draft outlives the panel. Closing a ticket by mistake — Escape, a click on
// the backdrop, a tab reload — must not throw away a reply that took ten
// minutes to write. Per case, per browser; cleared on send.
const draftKey = (caseId) => `crm.caseDraft.${caseId}`;
const readDraft = (caseId) => { try { return localStorage.getItem(draftKey(caseId)) || ''; } catch { return ''; } };
const writeDraft = (caseId, text) => { try { text ? localStorage.setItem(draftKey(caseId), text) : localStorage.removeItem(draftKey(caseId)); } catch { /* storage unavailable — the draft lives in state only */ } };

function useNarrow(px = 980) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${px}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${px}px)`);
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [px]);
  return narrow;
}

// ─── Case Detail Workspace ────────────────────────────────────────────────────
//
// The panel used to be a 520px drawer with a three-row textarea at the bottom
// of a long scroll: you could read the ticket OR write the reply, not both,
// and a reply longer than three lines was written through a letterbox. This
// is a workspace instead — the thread on the left, the case's facts and files
// on the right, and a composer that grows with what is being written, with
// the formatting the customer will actually see.

function CaseDetail({ caseId, onClose, onUpdated }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  // Seeded from storage on the FIRST render, not restored by an effect. The
  // restore effect below still handles moving between cases, but it cannot be
  // what fills the box on mount: the persist effect runs in the same commit
  // with `comment` still at its initial value, so an empty initial state
  // writes that empty string straight over the draft it was about to restore.
  // Nobody noticed while every draft was one the composer itself had written
  // (restore-then-rewrite happened to land right); a draft put there by
  // something else — the Today rail seeding a hello — was deleted before it
  // was ever shown.
  const [comment, setComment]   = useState(() => readDraft(caseId));
  const [posting, setPosting]   = useState(false);
  const [status, setStatus]     = useState('');
  const [priority, setPriority] = useState('');
  const [dirty, setDirty]       = useState(false);
  // 'internal' | 'customer'. INTERNAL IS THE DEFAULT AND MUST STAY THE DEFAULT:
  // this box has always been an internal notepad, agents have years of habit
  // pointed at it, and the failure mode of the wrong default is publishing
  // triage reasoning to the customer — which cannot be taken back.
  const [visibility, setVisibility] = useState('internal');
  // The customer-reply confirmation step. A public comment is the customer's
  // to read the moment it lands and nothing can unsend it, so Send first shows
  // the reply rendered as they will see it, addressed, and asks once.
  const [review, setReview]       = useState(false);
  const [preview, setPreview]     = useState(false);
  const [linkForm, setLinkForm]   = useState(null); // { text, url } while the link dialog is open
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving]   = useState(null); // id of the file being taken off the case
  const [lightbox, setLightbox]   = useState(null); // the image file open large, or null
  const [notice, setNotice]       = useState('');
  const [restored, setRestored]   = useState(false);
  // Silent (Case.Staff_Only__c) ticket? null until that field is deployed.
  const [staffOnly, setStaffOnly] = useState(null);
  const [linking, setLinking]     = useState(false);
  // Send & close: the customer reply IS the resolution. Set by the button that
  // asks for it, read once the reply has gone out; reset everywhere else.
  const [closeAfterSend, setCloseAfterSend] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const threadEndRef = useRef(null);
  const narrow = useNarrow();
  // On a phone the rail stacked above the thread leaves the conversation a
  // sliver of screen; it folds behind one toggle there, shut by default.
  const phone = useNarrow(768);
  const [railOpen, setRailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/cases/${caseId}`, { headers: getHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load case');
      setData(json);
      setStaffOnly(json.staffOnly ?? null);
      setLinking(false);
      setCloseAfterSend(false);
      setStatus(json.case.Status || 'New');
      setPriority(json.case.Priority || 'Medium');
      setDirty(false);
      // Never carry a customer-facing composer across cases. Opening the next
      // ticket must not inherit "this one is going to the customer".
      setVisibility('internal');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // The draft for THIS case, if one was left behind.
  useEffect(() => {
    const saved = readDraft(caseId);
    setComment(saved);
    setRestored(Boolean(saved));
    setReview(false);
    setPreview(false);
    setLinkForm(null);
  }, [caseId]);
  useEffect(() => { writeDraft(caseId, comment); }, [caseId, comment]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      // A dialog inside the composer takes the first Escape.
      if (linkForm) { setLinkForm(null); return; }
      if (review) { setReview(false); return; }
      onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, linkForm, review]);

  // The composer grows with the reply — up to about half the viewport, after
  // which it scrolls — instead of holding three lines and a scrollbar.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = Math.round(window.innerHeight * 0.5);
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 180), max)}px`;
  }, [comment, preview, review, visibility]);

  // Land on the newest thing when the thread loads or grows.
  useEffect(() => { threadEndRef.current?.scrollIntoView({ block: 'end' }); }, [data]);

  // The open case is the most specific thing on screen — level 2 so it sits
  // above the ticket list in the ~ console's breadcrumb.
  useViewContext({
    label: data?.case ? `Case #${data.case.CaseNumber}` : 'Case',
    view: 'case-detail',
    caseId,
    caseNumber: data?.case?.CaseNumber || null,
    caseSubject: data?.case?.Subject || null,
    caseStatus: data?.case?.Status || null,
    casePriority: data?.case?.Priority || null,
    caseOrigin: data?.case?.Origin || null,
    caseCommentCount: data?.comments?.length ?? null,
    caseAttachmentCount: data?.attachments?.length ?? null,
    accountId: data?.case?.AccountId || null,
    accountName: data?.case?.Account?.Name || null,
  }, 2);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/cases/${caseId}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ Status: status, Priority: priority }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      setDirty(false);
      onUpdated();
      await load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const commentMax = data?.limits?.commentMaxLength || COMMENT_MAX_FALLBACK;
  const overLimit = comment.trim().length > commentMax;

  const handleComment = async () => {
    if (!comment.trim() || overLimit) return;
    const toCustomer = visibility === 'customer';
    // A public comment is the customer's to read the moment it lands, and
    // nothing in the product can unsend it — CaseComment has no edit or delete
    // surface here, and their dashboard renders it on next load. So the first
    // press of Send on a customer reply opens the review step (the reply as
    // the customer will see it, with the address it goes to); only the second
    // press, from inside that step, sends.
    if (toCustomer && !review) { setReview(true); return; }
    setPosting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/cases/${caseId}/comments`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ body: comment.trim(), isPublic: toCustomer }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to post comment');
      setComment('');
      setVisibility('internal');
      setReview(false);
      setPreview(false);
      if (toCustomer) {
        setNotice(json.emailed
          ? (reach === 'email'
            ? `Sent — emailed to ${json.to}. They have no Squish dashboard, so the email is the only copy they get.`
            : `Sent — the customer sees it on their dashboard and it was emailed to ${json.to}.`)
          : (reach === 'email'
            ? `Posted on the case, but NOT emailed: ${json.reason || 'no address'}. They have no dashboard, so nothing reached them.`
            : `Posted to the customer's dashboard. Not emailed: ${json.reason || 'no address'}.`));
      } else {
        setNotice('');
      }
      // Send & close: the reply is out, so it is the resolution. A close that
      // fails must not read as a failed send — the customer already has it.
      if (toCustomer && closeAfterSend) {
        setCloseAfterSend(false);
        const cRes = await fetch(`${API}/api/cases/${caseId}`, {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({ Status: 'Closed' }),
        });
        if (cRes.ok) { onUpdated(); onClose(); setPosting(false); return; }
        const cJson = await cRes.json().catch(() => ({}));
        setError(`Reply sent, but the case did not close: ${cJson.error || cRes.status}. Close it from the rail.`);
      }
      await load();
    } catch (e) {
      setError(e.message);
    }
    setPosting(false);
  };

  const handleClose = async () => {
    const note = comment.trim();
    if (!note) {
      setError('Add a resolution note below before closing — closed cases need a record of how they were resolved.');
      return;
    }
    if (!window.confirm(closeConfirmText(customerCanSee))) return;
    setSaving(true);
    setError('');
    try {
      // Post the resolution note first so the close is never a shell.
      //
      // ALWAYS INTERNAL, whatever the composer is set to. The resolution note
      // is a record for the next agent — it says what we actually did and often
      // why, in staff language. A sign-off the customer should read is a
      // deliberate reply, written as one. The Close button is disabled while
      // the composer is in customer mode rather than silently refiling their
      // words as an internal note.
      const cRes = await fetch(`${API}/api/cases/${caseId}/comments`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ body: `Resolution: ${note}`, isPublic: false }),
      });
      if (!cRes.ok) throw new Error((await cRes.json()).error || 'Failed to post resolution note');
      const res = await fetch(`${API}/api/cases/${caseId}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ Status: 'Closed' }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to close');
      setComment('');
      onUpdated();
      onClose();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  // ── Files ──
  const uploadFile = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      // No Content-Type: the browser sets multipart/form-data with its boundary.
      const headers = getHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API}/api/cases/${caseId}/attachments`, { method: 'POST', headers, body: form });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      // Name the file in the reply, in the rep's own text where they can see
      // and edit it, rather than posting a robot line to the thread.
      const line = `Attached: ${json.attachment?.name || file.name}`;
      setComment(c => (c.trim() ? `${c.replace(/\s+$/, '')}\n\n${line}` : line));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Take a file off the case — a duplicate upload, the wrong file. It leaves
  // the customer's ticket too, so it asks first. When no other file of that
  // name is left, the composer's "Attached: <name>" line goes with it, so the
  // reply never names a file that is no longer there.
  const removeFile = async (file) => {
    if (removing) return;
    if (!window.confirm(`Remove ${file.name} from this case? The customer will no longer see it on their ticket.`)) return;
    setRemoving(file.id);
    setError('');
    try {
      const res = await fetch(`${API}/api/cases/${caseId}/attachments/${file.id}`, { method: 'DELETE', headers: getHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Could not remove the file');
      const name = String(file.name || '').toLowerCase();
      const sameNameLeft = (data?.attachments || []).some(f => f.id !== file.id && String(f.name || '').toLowerCase() === name);
      if (!sameNameLeft) {
        setComment(c => c.split('\n').filter(l => l.trim().toLowerCase() !== `attached: ${name}`).join('\n').replace(/\n{3,}/g, '\n\n').trimEnd());
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(null);
    }
  };

  // ── Markup helpers: operate on the textarea's selection ──
  const withSelection = (fn) => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : comment.length;
    const end   = el ? el.selectionEnd : comment.length;
    const { text, cursorStart, cursorEnd } = fn(comment, start, end);
    setComment(text);
    requestAnimationFrame(() => {
      const t = textareaRef.current;
      if (!t) return;
      t.focus();
      t.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const wrap = (mark, placeholder) => withSelection((text, s, e) => {
    const selected = text.slice(s, e) || placeholder;
    const next = `${text.slice(0, s)}${mark}${selected}${mark}${text.slice(e)}`;
    return { text: next, cursorStart: s + mark.length, cursorEnd: s + mark.length + selected.length };
  });

  const prefixLines = (prefixFor) => withSelection((text, s, e) => {
    // Expand to whole lines, then prefix each.
    const lineStart = text.lastIndexOf('\n', s - 1) + 1;
    const lineEndIdx = text.indexOf('\n', e);
    const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
    const block = text.slice(lineStart, lineEnd);
    const lines = block.split('\n').map((l, i) => `${prefixFor(i)}${l.replace(/^(\s*)([-*•]|\d+[.)])\s+/, '$1')}`);
    const replaced = lines.join('\n');
    const next = `${text.slice(0, lineStart)}${replaced}${text.slice(lineEnd)}`;
    return { text: next, cursorStart: lineStart, cursorEnd: lineStart + replaced.length };
  });

  const openLinkForm = () => {
    const el = textareaRef.current;
    const selected = el ? comment.slice(el.selectionStart, el.selectionEnd) : '';
    const looksLikeUrl = /^https?:\/\/\S+$/i.test(selected.trim());
    setLinkForm({ text: looksLikeUrl ? '' : selected, url: looksLikeUrl ? selected.trim() : '' });
  };

  const insertLink = () => {
    if (!linkForm) return;
    let url = linkForm.url.trim();
    if (url && !/^(https?:\/\/|mailto:)/i.test(url)) url = `https://${url}`;
    if (!/^(https?:\/\/[^\s()<>"'`]+|mailto:[^\s()<>"'`]+)$/i.test(url)) { setError('That link needs to be a full https:// address.'); return; }
    const label = linkForm.text.trim() || url;
    withSelection((text, s, e) => {
      const md = `[${label}](${url})`;
      return { text: `${text.slice(0, s)}${md}${text.slice(e)}`, cursorStart: s + md.length, cursorEnd: s + md.length };
    });
    setLinkForm(null);
  };

  const onComposerKey = (e) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    if (e.key === 'Enter') { e.preventDefault(); handleComment(); }
    else if (e.key.toLowerCase() === 'b') { e.preventDefault(); wrap('**', 'bold'); }
    else if (e.key.toLowerCase() === 'i') { e.preventDefault(); wrap('_', 'italic'); }
    else if (e.key.toLowerCase() === 'k') { e.preventDefault(); openLinkForm(); }
  };

  // A published comment reaches a human two ways: the customer's dashboard,
  // which lists their cases BY ContactId (squish-backend listCasesForContact),
  // or the case-reply email, which squish-crm-backend sends to Contact.Email or
  // SuppliedEmail. So "can the customer see this?" is not a question about
  // IsPublished alone — see customerReach. Cases opened through the anonymous
  // door have no ContactId by design, but they do carry the address typed in.
  const audience = caseAudience(data?.case, staffOnly);
  const reach = customerReach(data?.case, staffOnly);
  const customerCanSee = reach !== 'none';
  const customerName = data?.case?.Contact
    ? [data.case.Contact.FirstName, data.case.Contact.LastName].filter(Boolean).join(' ')
    : null;
  const target = replyTarget(data?.case);
  const toCustomer = visibility === 'customer';

  const selectStyle = {
    background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6,
    color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, padding: '6px 10px',
    cursor: 'pointer', outline: 'none', flex: 1, minWidth: 0,
  };
  const sectionLabel = { fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 };
  const smallGhostBtn = {
    padding: '6px 12px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
    borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)',
  };
  const primaryBtn = (enabled) => ({
    padding: '8px 18px', background: enabled ? 'var(--accent)' : 'var(--bg3)', color: enabled ? '#fff' : 'var(--text3)',
    border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: enabled ? 'pointer' : 'not-allowed',
    fontFamily: 'var(--font)', transition: 'all 0.15s',
  });

  const canSend = Boolean(comment.trim()) && !posting && !overLimit && !(toCustomer && !customerCanSee);

  // ── Right rail: status, facts, files ──
  const rail = data && !loading && (
    <aside style={{
      width: narrow ? 'auto' : 300, flexShrink: 0, overflowY: narrow ? 'visible' : 'auto',
      borderLeft: narrow ? 'none' : '1px solid var(--border)', borderBottom: narrow ? '1px solid var(--border)' : 'none',
      // Wrap only in the narrow ROW layout. In the 300px column, wrap sizes each
      // flex line to its widest child's natural width instead of the rail's, so
      // one long file name pushed every section past the edge and the rail
      // scrolled sideways.
      padding: narrow ? '12px 20px' : '18px 20px 24px', display: 'flex', flexDirection: narrow ? 'row' : 'column', flexWrap: narrow ? 'wrap' : 'nowrap', gap: narrow ? 20 : 22,
      background: 'var(--bg)',
    }}>
      {/* Status + Priority */}
      <section style={{ flex: narrow ? '1 1 260px' : 'none' }}>
        <div style={sectionLabel}>Status &amp; priority</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={status} onChange={e => { setStatus(e.target.value); setDirty(true); }} style={selectStyle} aria-label="Status">
            {['New', 'Working', 'Escalated', 'Closed'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priority} onChange={e => { setPriority(e.target.value); setDirty(true); }} style={selectStyle} aria-label="Priority">
            {['Low', 'Medium', 'High'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {dirty && (
            <button data-write onClick={handleSave} disabled={saving} style={{ ...primaryBtn(!saving), padding: '6px 14px' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
          {data.case.Status !== 'Closed' && (
            // Disabled while the composer is aimed at the customer. Close
            // files the note as an INTERNAL resolution record, so closing
            // with a customer reply in the box would quietly turn words
            // written to be read into words nobody outside will see.
            // Refusing is honest; reinterpreting is not.
            <button
              onClick={handleClose}
              disabled={saving || visibility === 'customer'}
              title={visibility === 'customer'
                ? 'Use Send & close in the composer — Close files the note internally, and the customer is not told'
                : 'Closes the case, filing the note in the composer as the resolution record'}
              style={{ ...smallGhostBtn, cursor: (saving || visibility === 'customer') ? 'not-allowed' : 'pointer', opacity: visibility === 'customer' ? 0.5 : 1, fontFamily: 'var(--mono)', fontSize: 11 }}
            >
              Close case
            </button>
          )}
        </div>
      </section>

      {/* Facts */}
      <section style={{ flex: narrow ? '1 1 220px' : 'none' }}>
        <div style={sectionLabel}>Details</div>
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 12 }}>
          {[
            ['Origin',   data.case.Origin],
            ['Account',  data.case.Account?.Name],
            ['Contact',  customerName],
            ['Phone',    data.case.SuppliedPhone],
            ['Opened',   formatDateTime(data.case.CreatedDate)],
            ['Closed',   data.case.ClosedDate ? formatDateTime(data.case.ClosedDate) : null],
          ].filter(([, v]) => v).map(([label, value]) => (
            <React.Fragment key={label}>
              <dt style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 11 }}>{label}</dt>
              <dd style={{ margin: 0, color: 'var(--text2)', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      {/* Files */}
      <section style={{ flex: narrow ? '1 1 260px' : 'none' }}>
        <div style={{ ...sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Files ({data.attachments?.length || 0})</span>
          <button data-write
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ ...smallGhostBtn, padding: '3px 9px', fontSize: 11, textTransform: 'none', letterSpacing: 0 }}
          >
            {uploading ? 'Uploading…' : '+ Attach'}
          </button>
        </div>
        {data.attachmentsUnavailable && (
          <div style={{ fontSize: 11, color: 'var(--badge-yellow)', marginBottom: 8 }}>⚠ The file list could not be loaded just now.</div>
        )}
        {!data.attachments?.length && !data.attachmentsUnavailable && (
          <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>No files on this case.</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(data.attachments || []).map(f => (
            <div key={f.id} style={{ display: 'flex', gap: 6, alignItems: 'stretch', minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AttachmentCard caseId={caseId} file={f} size="row" onOpen={setLightbox} onError={setError} />
              </div>
              <button data-write
                type="button"
                onClick={() => removeFile(f)}
                disabled={!!removing}
                title={`Remove ${f.name} from this case`}
                aria-label={`Remove ${f.name}`}
                style={{ ...smallGhostBtn, padding: '0 10px', fontSize: 12, flexShrink: 0, cursor: removing ? 'progress' : 'pointer' }}
              >
                {removing === f.id ? '…' : '✕'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8, lineHeight: 1.6 }}>
          Images open here; PDFs and text files download. Only images, PDFs and plain text can be on a case
          — anything else (.exe, .zip, Office, SVG, HTML) is refused at upload by its bytes, whatever it is named.
          Up to {humanBytes(data.limits?.attachmentMaxBytes || 10 * 1024 * 1024)} each, {data.limits?.attachmentsPerCase || 10} per case.
          The customer sees the same list on their ticket.
        </div>
      </section>
    </aside>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div
        role="dialog"
        aria-label={data?.case ? `Case #${data.case.CaseNumber}` : 'Case'}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(1240px, 100vw)',
          background: 'var(--bg)', borderLeft: '1px solid var(--border)',
          zIndex: 101, display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/avif,application/pdf,text/plain,.txt,.log,.csv"
          onChange={e => uploadFile(e.target.files?.[0])}
          style={{ display: 'none' }}
        />

        {/* Header */}
        <div style={{
          padding: '16px 24px 14px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {data && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>Case #{data.case.CaseNumber}</span>}
              {data && <span className={`badge ${statusBadge(data.case.Status)}`}>{data.case.Status}</span>}
              {data && <span className={`badge ${priorityBadge(data.case.Priority)}`}>{data.case.Priority}</span>}
              {data?.attachments?.length > 0 && (
                // Files are easy to miss in a rail; say so up top, where the
                // eye lands first.
                <span title="Files on this case — see the Files list" style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', background: 'rgba(124,109,250,0.12)', border: '1px solid rgba(124,109,250,0.35)', borderRadius: 999, padding: '2px 9px' }}>
                  📎 {data.attachments.length} {data.attachments.length === 1 ? 'file' : 'files'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35, marginTop: 4 }}>
              {loading ? 'Loading...' : (data?.case.Subject || '—')}
            </div>
            {data && (
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                Opened {formatDateTime(data.case.CreatedDate)}{data.case.Account?.Name ? ` · ${data.case.Account.Name}` : ''}
              </div>
            )}
            {/* An unlinked or silent case with no address says so on the
                audience line instead — "No reply address" alone hid the fix. */}
            {data && (target.email || (audience.kind !== 'unlinked' && audience.kind !== 'silent')) && <ReplyAddress c={data.case} />}
            {data && <AudienceLine audience={audience} onLink={() => setLinking(true)} />}
            {data && linking && audience.canLink && (
              <LinkCustomerForm
                caseId={caseId}
                silent={audience.kind === 'silent'}
                onCancel={() => setLinking(false)}
                onDone={async (msg) => { setLinking(false); setError(''); setNotice(msg); onUpdated(); await load(); }}
              />
            )}
          </div>
          <button onClick={onClose} aria-label="Close panel" title="Close (Esc) — your draft is kept" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        {loading && <div style={{ padding: 32 }}><Loading /></div>}
        {error && !data && <div className="error-state" style={{ margin: 20 }}>⚠ {error}</div>}

        {data && !loading && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: narrow ? 'column' : 'row' }}>
            {narrow && !phone && rail}
            {phone && (
              <>
                <button
                  type="button"
                  onClick={() => setRailOpen(o => !o)}
                  aria-expanded={railOpen}
                  style={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '10px 16px', background: 'var(--bg2)', border: 'none', borderBottom: '1px solid var(--border)',
                    color: 'var(--text2)', fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span>{railOpen ? '▾' : '▸'}</span>
                  <span>{railOpen ? 'Hide details' : 'Status, details & files'}</span>
                </button>
                {railOpen && <div style={{ flexShrink: 0, maxHeight: '55vh', overflowY: 'auto' }}>{rail}</div>}
              </>
            )}

            {/* Main column: thread + composer */}
            <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Thread */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 24px' }}>
                {data.case.Description && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={sectionLabel}>Description</div>
                    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
                      <ReplyMarkup
                        text={data.case.Description}
                        inlineMarkup={false}
                        style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}
                      />
                    </div>
                  </div>
                )}

                <div style={sectionLabel}>Comments ({data.comments?.length || 0})</div>
                {data.comments?.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No comments yet.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.comments?.map(cm => {
                    const who = classifyComment(cm.CommentBody);
                    if (who.author === 'activity') {
                      return (
                        <div key={cm.Id} style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '2px 0', fontFamily: 'var(--mono)' }}>
                          {who.body} · {formatDateTime(cm.CreatedDate)}
                        </div>
                      );
                    }
                    const fromCustomer = who.author === 'customer';
                    // A comment the customer can read is bordered in the accent,
                    // the same colour the composer wears when it is aimed at them.
                    // Scanning the feed should answer "what have they actually
                    // seen?" without reading a label on every row.
                    return (
                      <div key={cm.Id} style={{ background: 'var(--bg2)', border: `1px solid ${cm.IsPublished ? 'var(--accent)' : 'var(--border)'}`, borderLeftWidth: fromCustomer ? 3 : 1, borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: cm.IsPublished ? 'var(--accent)' : 'var(--text)' }}>
                            {fromCustomer
                              ? `👤 ${who.authorName || 'Customer'}${who.onBehalf ? ' (via support)' : ''}${who.via ? ` · ${who.via}` : ''}`
                              : cm.IsPublished ? '🌐 Sent to customer' : '🔒 Internal'}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{formatDateTime(cm.CreatedDate)}</span>
                        </div>
                        <ReplyMarkup
                          text={who.body}
                          // The customer's words are shown as typed; ours carry the markup.
                          inlineMarkup={!fromCustomer}
                          style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.65 }}
                        />
                        {/* The file the comment talks about, right under it — an
                            image shows itself, anything else is a chip. */}
                        {(() => {
                          const files = filesMentioned(who.body, data.attachments);
                          return files.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                              {files.map(f => <AttachmentCard key={f.id} caseId={caseId} file={f} size="thumb" onOpen={setLightbox} onError={setError} />)}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
                <div ref={threadEndRef} />
              </div>

              {/* Composer */}
              <div style={{ borderTop: '1px solid var(--border)', padding: '12px 24px 16px', flexShrink: 0, background: 'var(--bg)', maxHeight: '75vh', overflowY: 'auto' }}>
                {error && <div className="error-state" style={{ marginBottom: 10 }}>⚠ {error}</div>}
                {notice && !error && (
                  <div style={{ fontSize: 12, color: 'var(--accent3)', marginBottom: 10, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span>✓ {notice}</span>
                    <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12 }}>dismiss</button>
                  </div>
                )}

                {review ? (
                  // ── Review step: the reply as the customer will see it ──
                  <div style={{ border: '1px solid var(--accent)', borderRadius: 8, padding: '14px 16px', background: 'var(--bg2)' }}>
                    <div style={{ ...sectionLabel, color: 'var(--accent)' }}>Review before sending</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.6 }}>
                      To <strong style={{ color: 'var(--text)' }}>{customerName || target.name || 'the customer'}</strong>
                      {target.email ? <> · <span style={{ fontFamily: 'var(--mono)' }}>{target.email}</span> by email</> : <> · <span style={{ color: 'var(--badge-yellow)' }}>no email address — dashboard only</span></>}
                      , and on their Squish dashboard. It cannot be edited or deleted once sent.
                      {closeAfterSend && <> Then the case closes, with this reply as the resolution.</>}
                    </div>
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', maxHeight: '45vh', overflowY: 'auto' }}>
                      <ReplyMarkup text={comment} style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <button onClick={() => { setReview(false); setCloseAfterSend(false); }} disabled={posting} style={smallGhostBtn}>Keep editing</button>
                      <button data-write onClick={handleComment} disabled={posting} style={primaryBtn(!posting)}>
                        {posting ? 'Sending…' : `Send to ${customerName?.split(' ')[0] || 'customer'}${closeAfterSend ? ' & close' : ''}`}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                      <VisibilityTabs
                        value={visibility}
                        onChange={setVisibility}
                        reachable={customerCanSee}
                      />
                      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, flex: '1 1 240px' }}>
                        {toCustomer ? (
                          reach === 'dashboard' ? (
                            <>Goes to <strong style={{ color: 'var(--text2)' }}>{customerName || 'the customer'}</strong> on their dashboard and by email. You will review it before it sends.</>
                          ) : reach === 'email' ? (
                            // Web-form inquiry: no Contact, so no dashboard thread, but
                            // the backend mails SuppliedEmail. Say the words leave by
                            // email only, so the rep writes a complete answer.
                            <>Goes to <strong style={{ color: 'var(--text2)' }}>{target.name || target.email}</strong> by email only — they have no Squish dashboard. You will review it before it sends.</>
                          ) : (
                            // The case has no ContactId, so the customer's dashboard —
                            // which lists their cases BY ContactId — can never show this
                            // thread to anybody. A "public" comment here would be written
                            // into a room with no one in it, and the agent would have no
                            // way to tell. Say so instead of letting them find out by
                            // never getting a reply.
                            <span style={{ color: 'var(--badge-yellow)' }}>⚠ No contact or email on this case, so nothing posted here reaches anybody. {audience.canLink ? 'Use Link to customer above first' : 'Find another way to reach them'}.</span>
                          )
                        ) : (
                          <>Staff only. The customer never sees this{data.case.Status !== 'Closed' ? ' — and it doubles as the resolution note on Close case' : ''}.</>
                        )}
                      </div>
                    </div>

                    {/* Toolbar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', padding: '4px 6px', background: 'var(--bg2)', border: `1px solid ${toCustomer ? 'var(--accent)' : 'var(--border)'}`, borderBottom: 'none', borderRadius: '8px 8px 0 0' }}>
                      <ToolButton title="Bold (Ctrl+B)" onClick={() => wrap('**', 'bold')}><strong>B</strong></ToolButton>
                      <ToolButton title="Italic (Ctrl+I)" onClick={() => wrap('_', 'italic')}><em style={{ fontFamily: 'serif' }}>I</em></ToolButton>
                      <ToolButton title="Code" onClick={() => wrap('`', 'code')}><span style={{ fontFamily: 'var(--mono)' }}>{'</>'}</span></ToolButton>
                      <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
                      <ToolButton title="Link (Ctrl+K)" onClick={openLinkForm} active={Boolean(linkForm)}>🔗 Link</ToolButton>
                      <ToolButton title="Bulleted list" onClick={() => prefixLines(() => '- ')}>• List</ToolButton>
                      <ToolButton title="Numbered list" onClick={() => prefixLines(i => `${i + 1}. `)}>1. List</ToolButton>
                      <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
                      <ToolButton title="Attach a file to this case" onClick={() => fileInputRef.current?.click()}>{uploading ? '⏳ Uploading…' : '📎 Attach'}</ToolButton>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                        <ToolButton title="Write" onClick={() => setPreview(false)} active={!preview}>Write</ToolButton>
                        <ToolButton title="Preview — as the customer will see it" onClick={() => setPreview(true)} active={preview}>Preview</ToolButton>
                      </div>
                    </div>

                    {linkForm && (
                      <div
                        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', background: 'var(--bg3)', borderLeft: `1px solid ${toCustomer ? 'var(--accent)' : 'var(--border)'}`, borderRight: `1px solid ${toCustomer ? 'var(--accent)' : 'var(--border)'}` }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertLink(); } }}
                      >
                        <input
                          autoFocus
                          placeholder="Link text (optional)"
                          value={linkForm.text}
                          onChange={e => setLinkForm({ ...linkForm, text: e.target.value })}
                          style={{ flex: '1 1 160px', padding: '6px 10px', fontSize: 12, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontFamily: 'var(--font)', outline: 'none' }}
                        />
                        <input
                          placeholder="https://…"
                          value={linkForm.url}
                          onChange={e => setLinkForm({ ...linkForm, url: e.target.value })}
                          style={{ flex: '2 1 240px', padding: '6px 10px', fontSize: 12, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontFamily: 'var(--mono)', outline: 'none' }}
                        />
                        <button type="button" onClick={insertLink} style={{ ...primaryBtn(true), padding: '6px 12px' }}>Insert</button>
                        <button type="button" onClick={() => setLinkForm(null)} style={{ ...smallGhostBtn, padding: '5px 10px' }}>Cancel</button>
                      </div>
                    )}

                    {preview ? (
                      <div style={{
                        minHeight: 180, background: 'var(--bg2)', border: `1px solid ${toCustomer ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: '0 0 8px 8px', padding: '12px 14px', fontSize: 13, color: 'var(--text)', lineHeight: 1.7,
                      }}>
                        {comment.trim()
                          ? <ReplyMarkup text={comment} />
                          : <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>Nothing to preview yet.</span>}
                      </div>
                    ) : (
                      <textarea
                        ref={textareaRef}
                        value={comment}
                        onChange={e => { setComment(e.target.value); setRestored(false); }}
                        placeholder={toCustomer
                          ? 'Write your reply to the customer… Links as [text](https://…), **bold**, - lists. Preview shows it as they will see it.'
                          : 'Write an internal note…'}
                        spellCheck
                        style={{
                          width: '100%', boxSizing: 'border-box', display: 'block', background: 'var(--bg2)',
                          // The composer changes colour with its audience. A reply that
                          // is about to leave the building should not look identical to
                          // a note that is not.
                          border: `1px solid ${toCustomer ? 'var(--accent)' : 'var(--border)'}`,
                          borderRadius: '0 0 8px 8px', padding: '12px 14px', color: 'var(--text)',
                          fontFamily: 'var(--font)', fontSize: 13, lineHeight: 1.65, resize: 'vertical', outline: 'none',
                          minHeight: 180,
                        }}
                        onKeyDown={onComposerKey}
                      />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        {restored && comment ? 'Draft restored · ' : ''}
                        <span style={{ color: overLimit ? 'var(--accent2)' : comment.length > commentMax * 0.9 ? 'var(--badge-yellow)' : 'var(--text3)' }}>
                          {comment.trim().length.toLocaleString()} / {commentMax.toLocaleString()}
                        </span>
                        {overLimit ? ' — too long for one comment' : ''}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', flex: 1 }}>Ctrl+Enter to {toCustomer ? 'review' : 'post'}</span>
                      {comment && (
                        <button onClick={() => { if (window.confirm('Discard this draft?')) setComment(''); }} style={{ ...smallGhostBtn, padding: '6px 10px', fontSize: 11 }}>Discard</button>
                      )}
                      {toCustomer && data.case.Status !== 'Closed' && (
                        // The resolution goes to the customer: the reply is the
                        // record, and it reaches them. Same review step as Send.
                        <button
                          onClick={() => { setCloseAfterSend(true); handleComment(); }}
                          disabled={!canSend}
                          title="Review, send this reply to the customer, then close the case with it as the resolution"
                          style={{ ...smallGhostBtn, padding: '8px 14px', opacity: canSend ? 1 : 0.5, cursor: canSend ? 'pointer' : 'not-allowed' }}
                        >
                          Send &amp; close
                        </button>
                      )}
                      <button
                        onClick={() => { setCloseAfterSend(false); handleComment(); }}
                        disabled={!canSend}
                        style={primaryBtn(canSend)}
                      >
                        {posting ? 'Posting…' : toCustomer ? 'Review & send' : 'Post note'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {!narrow && rail}
          </div>
        )}
      </div>
      {lightbox && <Lightbox caseId={caseId} file={lightbox} onClose={() => setLightbox(null)} onError={setError} />}
    </>
  );
}

// ─── Cases List ───────────────────────────────────────────────────────────────

export default function Tickets({ pendingCaseId, onPendingCaseHandled }) {
  const [cases, setCases]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  // Open by default: the page is a work queue, and a closed ticket is history.
  // 'All' and 'Closed' are one click away in the filter.
  const [filterStatus, setFilterStatus] = useState('Open');
  const [selectedId, setSelectedId]     = useState(null);

  // Cross-page navigation hook: when another surface (Dashboard My Queue,
  // CustomerDetail Recent Activity, etc.) calls App's handleNavigateToTicket,
  // App passes that caseId in via pendingCaseId. We open the modal here, then
  // tell App to clear the pending state so a re-click on the sidebar Tickets
  // button doesn't auto-reopen whatever was last viewed.
  useEffect(() => {
    if (pendingCaseId) {
      setSelectedId(pendingCaseId);
      onPendingCaseHandled?.();
    }
  }, [pendingCaseId, onPendingCaseHandled]);

  const load = useCallback(() => {
    setLoading(true);
    fetchCases()
      .then(setCases)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const statuses = ['All', 'Open', 'Closed'];

  const filtered = cases.filter(c => {
    const matchSearch = `${c.CaseNumber} ${c.Subject} ${c.Account?.Name || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All'
      || (filterStatus === 'Open'   && c.Status !== 'Closed')
      || (filterStatus === 'Closed' && c.Status === 'Closed');
    return matchSearch && matchStatus;
  });

  const openCount    = cases.filter(c => c.Status !== 'Closed').length;
  const highPriority = cases.filter(c => c.Priority === 'High' && c.Status !== 'Closed').length;

  // What the ~ console should understand "these tickets" to mean — the filter
  // in effect, not the rows themselves (the agent re-queries for those).
  useViewContext({
    view: 'ticket-list',
    ticketFilter: filterStatus,
    ticketSearch: search || null,
    ticketsShown: filtered.length,
    totalCases: cases.length,
    openCases: openCount,
    highPriorityOpen: highPriority,
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Support Tickets</h1>
        <p className="page-subtitle">Cases from Salesforce Service Cloud</p>
      </div>

      <div className="stat-grid m-tiles" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Cases</div><div className="stat-value accent">{cases.length}</div></div>
        <div className="stat-card"><div className="stat-label">Open Cases</div><div className="stat-value pink">{openCount}</div></div>
        <div className="stat-card">
          <div className="stat-label">High Priority Open</div>
          <div className="stat-value" style={{ color: highPriority > 0 ? 'var(--accent2)' : 'var(--accent3)' }}>{highPriority}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="search-bar" placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 4 }}>
          {statuses.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, background: filterStatus === s ? 'var(--accent)' : 'transparent', color: filterStatus === s ? 'white' : 'var(--text2)', transition: 'all 0.15s' }}>{s}</button>
          ))}
        </div>
      </div>

      {error && <div className="error-state">⚠ {error}</div>}
      {loading && <Loading />}

      {!loading && (
        <div className="table-card">
          <div className="table-header">
            <span className="table-title">Cases</span>
            <span className="table-count">{filtered.length}</span>
          </div>
          <table className="m-cards">
            <thead>
              <tr><th>Case #</th><th>Subject</th><th>Account</th><th>Status</th><th>Priority</th><th>Origin</th><th>Opened</th></tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.Id} onClick={() => setSelectedId(c.Id)} style={{ cursor: 'pointer', background: selectedId === c.Id ? 'rgba(124,109,250,0.08)' : undefined, transition: 'background 0.1s' }}
                  onMouseEnter={e => { if (selectedId !== c.Id) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={e => { if (selectedId !== c.Id) e.currentTarget.style.background = ''; }}
                >
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{c.CaseNumber}</td>
                  <td className="m-card-title" style={{ color: 'var(--text)', fontWeight: 600, maxWidth: 260 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.Subject || '—'}</span>
                      {/* A ticket that arrived with a screenshot says so before it
                          is opened. attachmentCount is absent (not 0) when the
                          count query failed server-side, and absent means silent. */}
                      {c.attachmentCount > 0 && (
                        <span title={`${c.attachmentCount} ${c.attachmentCount === 1 ? 'file' : 'files'} on this case`} style={{ flexShrink: 0, fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600, color: 'var(--accent)', background: 'rgba(124,109,250,0.12)', border: '1px solid rgba(124,109,250,0.35)', borderRadius: 999, padding: '1px 7px' }}>
                          📎 {c.attachmentCount}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{c.Account?.Name || '—'}</td>
                  <td><span className={`badge ${statusBadge(c.Status)}`}>{c.Status || '—'}</span></td>
                  <td><span className={`badge ${priorityBadge(c.Priority)}`}>{c.Priority || '—'}</span></td>
                  <td style={{ color: 'var(--text3)', fontSize: 12 }}>{c.Origin || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{formatDate(c.CreatedDate)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>
                    {filterStatus === 'Open' && !search
                      ? <>Nothing open — the queue is clear. <button onClick={() => setFilterStatus('All')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}>Show all cases</button></>
                      : 'No cases found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <CaseDetail caseId={selectedId} onClose={() => setSelectedId(null)} onUpdated={load} />}
    </div>
  );
}
